import React, { Component } from 'react';
import { Button, InputGroup, Popover, Position, Tag } from '@blueprintjs/core';
import { observable, toJS } from 'mobx';
import { observer, inject } from 'mobx-react';
import ListPicker from 'components/ListPicker';
import SearchFiltersToolbar from 'components/SearchFiltersToolbar';
import SearchFiltersSearchButton from 'components/SearchFiltersSearchButton';
import { Api } from 'services/Api';

export const USER_TYPE_OPTIONS = [
  { label: 'Admins', value: 'admin' },
  { label: 'Technicians', value: 'technician' },
];

export const USER_STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

// Filters for the Users and Invitations lists. Changes stay in a draft until
// Search (or Enter) applies them: viewStore.users() puts them in the URL and
// the route loads the list (Router.js, /dashboard/users/:section). The page
// keys this bar on the URL, so the draft always starts from what's applied.
@inject('viewStore', 'dashboard') @observer
class UsersSearch extends Component {

  @observable draft = toJS(this.props.viewStore.userSearch);

  // Skills are free-form; the choices are every skill already in use here.
  @observable skillOptions = [];

  componentDidMount() {
    if (this.props.viewStore.userSearch.section === 'invitations') return;
    Api.get('/users/skills')
      .then((response) => {
        this.skillOptions = (response.data.skills || []).map((skill) => ({ label: skill, value: skill }));
      })
      .catch(() => {
        this.skillOptions = [];
      });
  }

  search = () => {
    const { viewStore, dashboard } = this.props;
    const before = viewStore.currentPath;
    viewStore.userSearch = { ...toJS(this.draft), section: viewStore.userSearch.section };
    viewStore.users();
    // Same filters as already applied: the URL doesn't change, so reload here.
    if (viewStore.currentPath === before) {
      dashboard.loadUsers({ section: viewStore.userSearch.section, where: viewStore.userSearch.where });
    }
  };

  setWhere(key, selections) {
    // An old link may not carry every list; start from what's there.
    this.draft.where = { ...this.draft.where, [key]: selections.map((option) => option.value) };
  }

  onTermChange = (event) => {
    this.draft.term = event.target.value;
  };

  onKeyDown = (event) => {
    if (event.key === 'Enter') this.search();
  };

  renderPicker(key, label, options) {
    const selected = toJS((this.draft.where && this.draft.where[key]) || []);
    return (
      <Popover
        minimal={true}
        position={Position.BOTTOM_LEFT}
        usePortal={true}
        content={
          <ListPicker
            selectedOptions={selected}
            options={options}
            onChange={(selections) => this.setWhere(key, selections)}
          />
        }
      >
        <Button rightIcon="caret-down">
          {label}
          <span>&nbsp;</span>
          <Tag minimal={true} round={true}>{selected.length}</Tag>
        </Button>
      </Popover>
    );
  }

  render() {
    const { viewStore } = this.props;
    const invitations = viewStore.userSearch.section === 'invitations';

    return (
      <SearchFiltersToolbar
        className="users-filter-bar"
        searchButton={<SearchFiltersSearchButton onClick={this.search} />}
        actions={
          <Button icon="plus" small={true} onClick={() => viewStore.inviteUsers()}>
            Invite user
          </Button>
        }
      >
        <InputGroup
          className="search-filters-toolbar__field-wide"
          leftIcon="search"
          placeholder={invitations ? 'Search by email' : 'Search by name, email or phone'}
          aria-label={invitations ? 'Search invitations' : 'Search users'}
          value={this.draft.term || ''}
          onChange={this.onTermChange}
          onKeyDown={this.onKeyDown}
        />
        {!invitations && this.renderPicker('role', 'User type', USER_TYPE_OPTIONS)}
        {!invitations && this.renderPicker('status', 'Status', USER_STATUS_OPTIONS)}
        {!invitations && this.renderPicker('skills', 'Skills', this.skillOptions)}
      </SearchFiltersToolbar>
    );
  }
}

export default UsersSearch;
